import { ethers } from "ethers";

export const ARBITRUM_CHAIN_ID = 42161;
export const ARBITRUM_CHAIN_ID_HEX = "0xa4b1";
export const ARBITRUM_RPC_URL = (
  (typeof process !== "undefined" && process.env
    && (process.env.REACT_APP_ARBITRUM_RPC_URL || process.env.VITE_ARBITRUM_RPC_URL))
  || "https://arb1.arbitrum.io/rpc"
);

export const ARBITRUM_CHAIN = {
  chainId: ARBITRUM_CHAIN_ID_HEX,
  chainName: "Arbitrum One",
  nativeCurrency: {
    name: "Ether",
    symbol: "ETH",
    decimals: 18,
  },
  rpcUrls: [ARBITRUM_RPC_URL],
  blockExplorerUrls: ["https://arbiscan.io"],
};

export const APTESG_ADDRESS = "0x58665Cf4742476532CA2387Bcc1631Af21C10c79";
export const WETH_ADDRESS = "0x82af49447d8a07e3bd95bd0d56f35241523fbab1";
export const APTESG_POOL_ADDRESS = "0xc4c49a3a1047fe21a3b40e34782583716d7ecd62";
export const UNISWAP_V3_FEE = 3000;
export const UNISWAP_V3_ROUTER = "0xE592427A0AEce92De3Edee1F18E0157C05861564";
export const UNISWAP_V3_QUOTER = "0xb27308f9F90D607463bb33eA1BeBb41C27CE5AB6";

let arbitrumReadProvider;

export const getArbitrumReadProvider = () => {
  if (!arbitrumReadProvider) {
    arbitrumReadProvider = new ethers.providers.StaticJsonRpcProvider(
      ARBITRUM_RPC_URL,
      { chainId: ARBITRUM_CHAIN_ID, name: "arbitrum" }
    );
    // The public endpoint is HTTP-only, so ethers watches logs by polling.
    arbitrumReadProvider.pollingInterval = 4000;
  }

  return arbitrumReadProvider;
};

export const getEip1193Provider = (walletClient, options = {}) => {
  const { allowWindowFallback = true } = options;
  if (walletClient?.transport?.provider?.request) return walletClient.transport.provider;
  if (walletClient?.transport?.value?.provider?.request) return walletClient.transport.value.provider;
  if (walletClient?.transport?.request) return walletClient.transport;
  if (walletClient?.request) return walletClient;
  if (allowWindowFallback && typeof window !== "undefined" && window.ethereum?.request) {
    return window.ethereum;
  }
  return null;
};

export const getWalletProvider = (walletClient) => {
  const eip1193 = getEip1193Provider(walletClient);
  return eip1193 ? new ethers.providers.Web3Provider(eip1193, "any") : null;
};

const getErrorCode = (error) => (
  error?.code
  ?? error?.cause?.code
  ?? error?.data?.originalError?.code
  ?? error?.error?.code
);

const isUnknownChainError = (error) => {
  const code = Number(getErrorCode(error));
  const message = String(error?.shortMessage || error?.message || "").toLowerCase();
  return code === 4902
    || message.includes("unrecognized chain")
    || message.includes("unknown chain")
    || message.includes("chain has not been added");
};

const readWalletChainId = async (provider) => {
  const chainId = await provider.request({ method: "eth_chainId" });
  return typeof chainId === "string" ? Number.parseInt(chainId, 16) : Number(chainId);
};

const waitForArbitrum = async (provider) => {
  for (let attempt = 0; attempt < 8; attempt += 1) {
    if (await readWalletChainId(provider) === ARBITRUM_CHAIN_ID) return;
    await new Promise((resolve) => setTimeout(resolve, 250));
  }

  throw new Error("The wallet did not switch to Arbitrum One.");
};

/**
 * Requests Arbitrum on the wallet's active connector and verifies the result.
 * The wallet still controls the confirmation UI and may reject the request.
 */
export const ensureArbitrumNetwork = async (walletClient, connector) => {
  let eip1193 = null;

  if (connector?.getProvider) {
    try {
      eip1193 = await connector.getProvider();
    } catch (error) {
      console.warn("Unable to resolve the active connector provider", error);
    }
  }

  if (!eip1193?.request) {
    eip1193 = getEip1193Provider(walletClient, {
      // With an active connector, switching a different injected wallet is unsafe.
      allowWindowFallback: !connector,
    });
  }
  if (!eip1193?.request) {
    throw new Error("No active wallet provider is available. Reconnect your wallet and try again.");
  }

  if (await readWalletChainId(eip1193) === ARBITRUM_CHAIN_ID) return eip1193;

  try {
    await eip1193.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: ARBITRUM_CHAIN_ID_HEX }],
    });
  } catch (error) {
    if (!isUnknownChainError(error)) throw error;

    await eip1193.request({
      method: "wallet_addEthereumChain",
      params: [ARBITRUM_CHAIN],
    });

    // EIP-3085 does not guarantee that adding a chain selects it.
    await eip1193.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: ARBITRUM_CHAIN_ID_HEX }],
    });
  }

  await waitForArbitrum(eip1193);
  return eip1193;
};

export const getFriendlyWalletError = (error) => {
  const code = Number(getErrorCode(error));
  if (code === 4001) return "The request was cancelled in your wallet.";
  if (code === -32002) return "A wallet request is already open. Complete it in your wallet.";
  return error?.shortMessage || error?.reason || error?.message || "The wallet request failed.";
};
